<template>
  <div>
    <h4 class="mb-1">
      Нормализация результатов
    </h4>
    <p class="text-muted">
      Дистанции из протоколов, длину которых не удалось понять по названию («Дети», «Большой», «Элита»). Пока длина
      неизвестна, эти результаты не попадают в личную статистику участников («Мои результаты»). Укажите длину — для
      одного старта или для всех стартов с таким названием дистанции; статистика пересчитается сразу.
    </p>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      {{ errorText }}
    </b-alert>

    <b-card title="Нераспознанные дистанции">
      <b-form-input
        v-model="startFilter"
        type="number"
        placeholder="ID старта (необязательно)"
        class="w-25 mb-1"
        @change="fetchUnknown"
      />
      <b-table
        :items="unknown"
        :fields="unknownFields"
        :busy="loading"
        striped
        responsive
        empty-text="Всё распознано"
      >
        <template #cell(start)="data">
          <strong>{{ data.item.startName || `Старт #${data.item.startId}` }}</strong>
          <div class="text-muted small">
            #{{ data.item.startId }} · {{ (data.item.startDate || '').slice(0, 10) }}
          </div>
        </template>
        <template #cell(form)="data">
          <div class="d-flex align-items-center flex-wrap">
            <b-form-input
              v-model="drafts[draftKey(data.item)].km"
              placeholder="км"
              size="sm"
              class="mr-50"
              style="width: 80px"
            />
            <b-form-select
              v-model="drafts[draftKey(data.item)].sport"
              :options="sportOptions"
              size="sm"
              class="mr-50"
              style="width: 150px"
            />
            <b-form-checkbox
              v-model="drafts[draftKey(data.item)].everyStart"
              class="mr-50"
            >
              для всех стартов
            </b-form-checkbox>
            <b-button
              size="sm"
              variant="primary"
              :disabled="saving"
              @click="saveAlias(data.item)"
            >
              Сохранить
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-card title="Синонимы дистанций">
      <b-table
        :items="aliases"
        :fields="aliasFields"
        striped
        responsive
        empty-text="Синонимов пока нет"
      >
        <template #cell(start_id)="data">
          {{ data.item.start_id ? `#${data.item.start_id}` : 'все старты' }}
        </template>
        <template #cell(length_m)="data">
          {{ data.item.length_m ? `${data.item.length_m} м` : '—' }}
        </template>
        <template #cell(sport)="data">
          {{ data.item.sport || 'вид спорта старта' }}
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="saving"
            @click="removeAlias(data.item)"
          >
            Удалить
          </b-button>
        </template>
      </b-table>
      <div class="d-flex align-items-center mt-1">
        <b-form-input
          v-model="rebuildId"
          type="number"
          placeholder="ID старта"
          class="mr-50"
          style="width: 160px"
        />
        <b-button
          variant="outline-primary"
          :disabled="!rebuildId || saving"
          @click="rebuild"
        >
          Пересчитать старт
        </b-button>
        <span class="text-muted small ml-1">после правки длины дистанции или вида спорта старта</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BTable,
} from 'bootstrap-vue'
import resultsApi from '@/services/results-api.service'

export default {
  name: 'ResultsNormalizationPage',
  components: {
    BAlert,
    BButton,
    BCard,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BTable,
  },
  data() {
    return {
      loading: false,
      saving: false,
      errorText: '',
      startFilter: '',
      rebuildId: '',
      unknown: [],
      aliases: [],
      drafts: {},
      sportOptions: [
        { value: null, text: 'вид спорта старта' },
        { value: 'run', text: 'бег' },
        { value: 'ski', text: 'лыжи' },
        { value: 'triathlon', text: 'триатлон' },
        { value: 'swim', text: 'плавание' },
        { value: 'bike', text: 'велоспорт' },
        { value: 'nordic_walking', text: 'скандинавская ходьба' },
        { value: 'multisport', text: 'мультиспорт (не в статистике)' },
      ],
      unknownFields: [
        { key: 'start', label: 'Старт' },
        { key: 'distance', label: 'Дистанция в протоколе' },
        { key: 'rows', label: 'Строк' },
        { key: 'form', label: 'Длина' },
      ],
      aliasFields: [
        { key: 'raw', label: 'Дистанция' },
        { key: 'start_id', label: 'Старт' },
        { key: 'length_m', label: 'Длина' },
        { key: 'sport', label: 'Вид спорта' },
        { key: 'actions', label: '' },
      ],
    }
  },
  created() {
    this.fetchUnknown()
    this.fetchAliases()
  },
  methods: {
    draftKey(item) {
      return `${item.startId}|${item.distance}`
    },
    async fetchUnknown() {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await resultsApi.getUnknownDistances({ startId: this.startFilter || undefined })
        const drafts = {}
        data.forEach(item => {
          drafts[this.draftKey(item)] = this.drafts[this.draftKey(item)] || { km: '', sport: null, everyStart: false }
        })
        this.drafts = drafts
        this.unknown = data
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Ошибка загрузки'
      } finally {
        this.loading = false
      }
    },
    async fetchAliases() {
      try {
        const { data } = await resultsApi.getAliases()
        this.aliases = data
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Ошибка загрузки синонимов'
      }
    },
    async saveAlias(item) {
      const draft = this.drafts[this.draftKey(item)]
      const km = Number(String(draft.km).replace(',', '.'))
      if (!(km > 0)) {
        this.errorText = 'Укажите длину дистанции в километрах'
        return
      }
      await this.run(() => resultsApi.saveAlias({
        startId: draft.everyStart ? 0 : item.startId,
        raw: item.distance,
        lengthM: Math.round(km * 1000),
        sport: draft.sport || undefined,
        kind: draft.sport === 'multisport' ? 'multisport' : undefined,
      }))
    },
    async removeAlias(alias) {
      await this.run(() => resultsApi.removeAlias(alias.id))
    },
    async rebuild() {
      await this.run(() => resultsApi.rebuildStart(this.rebuildId))
    },
    async run(action) {
      this.saving = true
      this.errorText = ''
      try {
        await action()
        await Promise.all([this.fetchUnknown(), this.fetchAliases()])
      } catch (e) {
        this.errorText = e.response?.data?.message || 'Не удалось сохранить'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
